<template>
  <div>
    <!-- Toolbar -->
    <div v-if="!usesNativeViewer" class="flex text-sm toolbar-styles items-center shadow-md">
      <!-- Page navigation -->
      <div class="pages-selector flex items-center">
        <button @click="firstPage" class="text-sm">
          <svg
            class="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m17 16-4-4 4-4m-6 8-4-4 4-4"
            />
          </svg>
        </button>
        <button @click="prevPage" class="flex items-center text-sm">
          <svg
            class="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m15 19-7-7 7-7"
            />
          </svg>
          <select
            v-model="pageNum"
            @change="changePage"
            class="px-2 py-1 rounded-sm bg-transparent border-gray bg-white border border-gray-300 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 block"
          >
            <option v-for="page in pageCount" :key="page" :value="page">
              Page {{ page }} of {{ pageCount }}
            </option>
          </select>
        </button>
        <button @click="nextPage" class="flex items-center text-sm">
          <svg
            class="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m9 5 7 7-7 7"
            />
          </svg>
        </button>
        <button @click="lastPage">
          <svg
            class="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m7 16 4-4-4-4m6 8 4-4-4-4"
            />
          </svg>
        </button>
      </div>

      <span class="text-3xl toolbar-spacer">|</span>

      <!-- Zoom -->
      <div class="scale-selector flex items-center">
        <button @click="zoomOut">
          <svg
            class="mr-1 w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 12h14"
            />
          </svg>
        </button>
        <select
          v-model.number="scale"
          @change="renderCurrentPage"
          class="px-2 py-1 rounded-sm bg-transparent border-gray bg-white border border-gray-300 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 block"
        >
          <option :value="0.5">50%</option>
          <option :value="0.75">75%</option>
          <option :value="1">100%</option>
          <option :value="1.25">125%</option>
          <option :value="1.5">150%</option>
          <option :value="2">200%</option>
        </select>
        <button @click="zoomIn">
          <svg
            class="ml-1 w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 12h14m-7 7V5"
            />
          </svg>
        </button>
      </div>

      <span class="text-3xl toolbar-spacer">|</span>

      <button @click="print">
        <svg
          class="w-6 h-6 text-gray-800 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linejoin="round"
            stroke-width="2"
            d="M16.444 18H19a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h2.556M17 11V5a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v6h10ZM7 15h10v4a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-4Z"
          />
        </svg>
      </button>
      <span class="text-3xl toolbar-spacer">|</span>
      <button @click="download">
        <svg
          class="w-6 h-6 text-gray-800 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 13V4M7 14H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2m-1-5-4 5-4-5m9 8h.01"
          />
        </svg>
      </button>
      <span class="text-3xl toolbar-spacer">|</span>
      <button @click="fullscreen">
        <svg
          class="w-6 h-6 text-gray-800 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 4H4m0 0v4m0-4 5 5m7-5h4m0 0v4m0-4-5 5M8 20H4m0 0v-4m0 4 5-5m7 5h4m0 0v-4m0 4-5-5"
          />
        </svg>
      </button>

      <span class="text-3xl toolbar-spacer">|</span>

      <!-- Search -->
      <div
        class="flex items-center bg-white rounded-sm px-1 py-1.5 border border-gray-300 text-gray-900 text-sm"
      >
        <button @click="search">
          <svg
            class="w-5 h-5 mr-2 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-width="2"
              d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
            />
          </svg>
        </button>
        <input
          v-model="searchTerm"
          @keyup.enter="search"
          type="text"
          class="bg-transparent"
          placeholder="Search in PDF"
        />
      </div>
    </div>

    <!-- Canvas -->
    <canvas v-if="!usesNativeViewer" ref="pdfCanvas" class="w-full p-8 pdf-viewer" />
    <iframe
      v-else
      :src="pdfUrl"
      class="w-full pdf-native-viewer"
      title="Source document PDF"
    ></iframe>
  </div>
</template>

<script>
export default {
  name: 'PdfViewer',
  props: {
    pdfUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      pdfDoc: null,
      pageCount: 0,
      pageNum: 1,
      scale: 1.7,
      searchTerm: '',
      highlights: []
    };
  },
  computed: {
    canvas() {
      return this.$refs.pdfCanvas;
    },
    usesNativeViewer() {
      try {
        return new URL(this.pdfUrl, window.location.href).origin !== window.location.origin;
      } catch (error) {
        return false;
      }
    }
  },
  mounted() {
    if (this.usesNativeViewer) return;

    if (typeof pdfjsLib !== 'undefined') {
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
    } else {
      console.warn('pdfjsLib is not defined. The PDF viewer will not work on this page.');
      return;
    }

    this.loadPdf();
  },
  methods: {
    async loadPdf() {
      if (!this.canvas) return;
      try {
        this.pdfDoc = await pdfjsLib.getDocument(this.pdfUrl).promise;
        this.pageCount = this.pdfDoc.numPages;
        // Populate page selector options via computed property; it's dynamic
        await this.renderPage(this.pageNum);
      } catch (err) {
        console.error('Failed to load PDF:', err);
      }
    },

    async renderPage(num, searchTerm = null) {
      if (!this.pdfDoc || !this.canvas) return;
      const page = await this.pdfDoc.getPage(num);
      const viewport = page.getViewport({ scale: this.scale });
      const canvas = this.canvas;
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const ctx = canvas.getContext('2d');

      const renderContext = {
        canvasContext: ctx,
        viewport
      };
      await page.render(renderContext).promise;

      // Highlights if searching
      if (searchTerm) {
        this.highlights = await this.findHighlights(page, searchTerm, viewport, canvas);
        this.drawHighlights(canvas, this.highlights);
      } else {
        this.highlights = [];
      }
    },

    async findHighlights(page, term, viewport, canvas) {
      const textContent = await page.getTextContent();
      const highlights = [];
      const regex = new RegExp(term, 'gi');
      const { transform } = viewport;

      textContent.items.forEach((item) => {
        const matches = [...item.str.matchAll(regex)];
        if (matches.length > 0) {
          const x = item.transform[4];
          const y = item.transform[5];
          const width = item.width;
          const height = item.height;

          const [highlightX, highlightY] = [
            transform[0] * x + transform[2] * y + transform[4],
            transform[1] * x + transform[3] * y + transform[5]
          ];

          highlights.push({
            x: highlightX,
            y: canvas.height - highlightY - height,
            width: transform[0] * width,
            height: transform[3] * height
          });
        }
      });
      return highlights;
    },

    drawHighlights(canvas, highlights) {
      const ctx = canvas.getContext('2d');
      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 0, 0.4)';
      highlights.forEach(({ x, y, width, height }) => {
        ctx.fillRect(x, y, width, height);
      });
      ctx.restore();
    },

    async renderCurrentPage() {
      await this.renderPage(this.pageNum);
    },

    async changePage() {
      await this.renderPage(this.pageNum);
    },

    async search() {
      const term = this.searchTerm.trim();
      if (!term) return;
      await this.renderPage(this.pageNum, term);
    },

    // Navigation
    async firstPage() {
      this.pageNum = 1;
      await this.renderPage(1);
    },
    async lastPage() {
      this.pageNum = this.pageCount;
      await this.renderPage(this.pageCount);
    },
    async prevPage() {
      if (this.pageNum > 1) {
        this.pageNum--;
        await this.renderPage(this.pageNum);
      }
    },
    async nextPage() {
      if (this.pageNum < this.pageCount) {
        this.pageNum++;
        await this.renderPage(this.pageNum);
      }
    },

    // Zoom
    async zoomIn() {
      this.scale += 0.25;
      await this.renderCurrentPage();
    },
    async zoomOut() {
      if (this.scale > 0.5) {
        this.scale -= 0.25;
        await this.renderCurrentPage();
      }
    },

    // Actions
    print() {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = this.pdfUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.print();
      };
    },

    download() {
      const link = document.createElement('a');
      link.href = this.pdfUrl;
      link.download = 'document.pdf';
      link.click();
    },

    fullscreen() {
      if (this.canvas.requestFullscreen) {
        this.canvas.requestFullscreen();
      } else if (this.canvas.webkitRequestFullscreen) {
        this.canvas.webkitRequestFullscreen();
      }
    }
  }
};
</script>

<style scoped>
.pdf-native-viewer {
  height: 80vh;
  min-height: 640px;
  border: 1px solid #b8b8b8;
}
</style>
